// SEO metadata manager for the BiteTrack SPA.
//
// Static crawlers read the tags baked into index.html. At runtime this module
// keeps document.title and the meta tags in sync with the active app mode.
// Demo mode is a public showcase (the owner may use it as the landing
// experience), so its SEO content explicitly says "demo" and stays indexable.

export type SeoMode = "default" | "demo";

export type SeoContent = {
  readonly title: string;
  readonly description: string;
  readonly robots: string;
};

export const SEO_SITE_NAME = "BiteTrack";
export const OG_IMAGE_PATH = "og-cover.png";

export const SEO_CONTENT: Record<SeoMode, SeoContent> = {
  default: {
    title: "BiteTrack — AI Calorie & Macro Tracker",
    description:
      "BiteTrack is an AI-powered calorie, macro and fibre tracker. Log meals by photo, voice, barcode or chat, follow a personalised nutrition plan, and track weight and progress over time.",
    robots: "index, follow",
  },
  demo: {
    title: "BiteTrack Demo — Interactive AI Calorie & Macro Tracker",
    description:
      "Explore the BiteTrack demo: an interactive showcase of AI calorie and macro tracking with sample meals, weight trends, plan targets and an AI chat coach — no sign-up required.",
    robots: "index, follow",
  },
};

export type SeoMetaTarget =
  | { readonly kind: "name"; readonly key: string }
  | { readonly kind: "property"; readonly key: string };

export type SeoMutation =
  | { readonly type: "title"; readonly value: string }
  | { readonly type: "meta"; readonly target: SeoMetaTarget; readonly content: string }
  | { readonly type: "canonical"; readonly href: string };

export function normalizePageUrl(rawUrl: string): string {
  const url = new URL(rawUrl);
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function buildSeoMutations(mode: SeoMode, rawPageUrl: string): readonly SeoMutation[] {
  const content = SEO_CONTENT[mode];
  const pageUrl = normalizePageUrl(rawPageUrl);
  const imageUrl = new URL(OG_IMAGE_PATH, pageUrl).toString();
  return [
    { type: "title", value: content.title },
    { type: "meta", target: { kind: "name", key: "description" }, content: content.description },
    { type: "meta", target: { kind: "name", key: "robots" }, content: content.robots },
    { type: "meta", target: { kind: "property", key: "og:title" }, content: content.title },
    { type: "meta", target: { kind: "property", key: "og:description" }, content: content.description },
    { type: "meta", target: { kind: "property", key: "og:type" }, content: "website" },
    { type: "meta", target: { kind: "property", key: "og:site_name" }, content: SEO_SITE_NAME },
    { type: "meta", target: { kind: "property", key: "og:url" }, content: pageUrl },
    { type: "meta", target: { kind: "property", key: "og:image" }, content: imageUrl },
    { type: "meta", target: { kind: "name", key: "twitter:card" }, content: "summary_large_image" },
    { type: "meta", target: { kind: "name", key: "twitter:title" }, content: content.title },
    { type: "meta", target: { kind: "name", key: "twitter:description" }, content: content.description },
    { type: "meta", target: { kind: "name", key: "twitter:image" }, content: imageUrl },
    { type: "canonical", href: pageUrl },
  ];
}

export type SeoDomAdapter = {
  readonly setTitle: (title: string) => void;
  readonly setMeta: (target: SeoMetaTarget, content: string) => void;
  readonly setCanonical: (href: string) => void;
};

export function applySeoMutations(mutations: readonly SeoMutation[], adapter: SeoDomAdapter): void {
  for (const mutation of mutations) {
    if (mutation.type === "title") adapter.setTitle(mutation.value);
    else if (mutation.type === "meta") adapter.setMeta(mutation.target, mutation.content);
    else adapter.setCanonical(mutation.href);
  }
}

export function createDocumentSeoAdapter(doc: Document): SeoDomAdapter {
  return {
    setTitle: (title) => {
      doc.title = title;
    },
    setMeta: (target, content) => {
      const selector =
        target.kind === "name" ? `meta[name="${target.key}"]` : `meta[property="${target.key}"]`;
      let element = doc.head.querySelector<HTMLMetaElement>(selector);
      if (element === null) {
        element = doc.createElement("meta");
        element.setAttribute(target.kind, target.key);
        doc.head.appendChild(element);
      }
      element.content = content;
    },
    setCanonical: (href) => {
      let link = doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (link === null) {
        link = doc.createElement("link");
        link.rel = "canonical";
        doc.head.appendChild(link);
      }
      link.href = href;
    },
  };
}

export function syncSeoMetadata(mode: SeoMode): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    const mutations = buildSeoMutations(mode, window.location.href);
    applySeoMutations(mutations, createDocumentSeoAdapter(document));
    console.log("[Frontend][Seo] Applied SEO metadata", { mode, title: SEO_CONTENT[mode].title });
  } catch (error: unknown) {
    console.warn("[Frontend][Seo] SEO metadata could not be applied", {
      mode,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
